import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { format } from "date-fns";

import {
  FaFilter,
  FaColumns,
  FaExpand,
  FaCompress,
  FaLayerGroup,
} from "react-icons/fa";
import {
  MdOutlineSegment,
  MdOutlineFileDownload,
  MdOutlineLabel,
} from "react-icons/md";
import {
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";

import ModifyColumns from "./Tools/ModifyColumns";
import { DateRangeSelector } from "./../styles/CustomDateRangePicker";

import {
  apiRequestBodyBuilder,
  getPriorityColumnsFromStorage,
  getSortableValueGLOBAL,
  formatSegmentData,
} from "./config/tableHelper";

import {
  LABEL_COLUMNS,
  GROUP_COLUMNS,
  CAMPAIGN_COLUMNS,
  LABEL_SEGMENT_COLUMNS,
  GROUP_SEGMENT_COLUMNS,
} from "./Components/PromoRecap/PromoColumns";
import { RenderTable } from "./config/tableWrapper";
import { generatePDF, generateCSV } from "./Components/DownloadFuncs";

// Skeleton styled identically to PaidAdsPage TableSkeleton
function TableSkeleton() {
  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      {/* Header Bar */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-100 animate-pulse">
        <div className="h-4 w-32 bg-gray-200 rounded" />
        <div className="flex items-center gap-8 sm:gap-12">
          <div className="h-4 w-16 bg-gray-200 rounded hidden sm:block" />
          <div className="h-4 w-16 bg-gray-200 rounded" />
          <div className="h-4 w-20 bg-gray-200 rounded" />
          <div className="h-4 w-24 bg-gray-200 rounded hidden md:block" />
        </div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-gray-100 animate-pulse">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
          <div key={item} className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div
                className="h-4 bg-gray-200 rounded"
                style={{ width: `${140 + (item % 3) * 35}px` }}
              />
            </div>
            <div className="flex items-center gap-8 sm:gap-12">
              <div className="h-4 w-14 bg-gray-100 rounded hidden sm:block" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-20 bg-gray-100 rounded hidden md:block" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const PromoRecap = () => {
  const [segmentActive, setSegmentActive] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState("");
  const [showSegmentDropdown, setShowSegmentDropdown] = useState(false);
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [columns, setColumns] = useState([]);
  const [total, setTotal] = useState({});
  const [totalShow, setTotalShow] = useState(true);
  const [tableVisible, setTableVisible] = useState(true);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [random, setRandom] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);

  const [labelType, setLabelType] = useState("ads");
  const [showLabelTypeDropdown, setShowLabelTypeDropdown] = useState(false);

  let today = new Date();
  let year = today.getFullYear();
  let month = today.getMonth();
  const [state, setState] = useState([
    {
      startDate: new Date(year, month, 1),
      endDate: today,
      key: "selection",
    },
  ]);

  const [labels, setLabels] = useState([]);
  const [groups, setGroups] = useState([]);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState();
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [currentGroupType, setCurrentGroupType] = useState(null);
  const [campaignNameInput, setCampaignNameInput] = useState("");
  const [currentOrderType, setCurrentOrderType] = useState(true);
  const [currentOrderVariable, setCurrentOrderVariable] = useState(null);

  const filterRef = useRef(null);
  const segmentRef = useRef(null);
  const columnRef = useRef(null);
  const datePickerRef = useRef(null);
  const exportRef = useRef(null);
  const downloadRef = useRef(null);
  const labelTypeRef = useRef(null);

  const handleDownload = async (type) => {
    setShowDownloadMenu(false);
    setIsDownloading(true);
    try {
      const filename = `Promo_Recap_Report_${format(new Date(), "yyyy-MM-dd")}.${type}`;
      const dateRangeStr =
        state[0].startDate === state[0].endDate
          ? "All time"
          : `${format(state[0].startDate, "MMM dd, yyyy")} - ${format(state[0].endDate, "MMM dd, yyyy")}`;

      await new Promise((resolve) => setTimeout(resolve, 100));

      if (type === "pdf") {
        await generatePDF(
          data,
          columns,
          dateRangeStr,
          filename,
          "Promo report",
        );
      } else if (type === "csv") {
        await generateCSV(
          data,
          columns,
          dateRangeStr,
          filename,
          "Promo report",
        );
      }
    } catch (error) {
      console.error(`Failed to generate ${type.toUpperCase()}:`, error);
    } finally {
      setIsDownloading(false);
    }
  };

  const fetchLabels = async () => {
    try {
      let reqBody = apiRequestBodyBuilder(null, { type: labelType });
      const response = await axios.post(
        "https://api.confidanto.com/labels/get-all-labels",
        reqBody,
      );
      setLabels(response.data);
    } catch (err) {
      setLabels([]);
    }
  };

  const fetchGroups = async () => {
    try {
      let requestBody = apiRequestBodyBuilder();

      const infoResponse = await axios.post(
        "https://api.confidanto.com/campaign-group2/group-info",
        requestBody,
      );
      const groupType = infoResponse.data?.type;
      setCurrentGroupType(groupType);

      const typeForQuery = groupType === "keyword" ? "keywords" : groupType;

      requestBody.type = typeForQuery;
      const response = await axios.post(
        "https://api.confidanto.com/campaign-group/fetch-groups",
        requestBody,
      );
      setGroups(response.data.groups);
    } catch (err) {
      setGroups([]);
    }
  };

  const fetchData = async (currentAppliedFilters = []) => {
    const filterType = currentAppliedFilters[0]?.type;
    const filterValues = currentAppliedFilters.map((item) => item.value);

    let url = "https://api.confidanto.com/labels/datewise-data";
    let filterByRequestBody = { filter_by: filterType };

    if (filterType === "labels") {
      setColumns(LABEL_COLUMNS);
      filterByRequestBody.labels = filterValues;
      filterByRequestBody.type = labelType;
    } else if (filterType === "groups") {
      url = "https://api.confidanto.com/labels-group/datewise-data";
      setColumns(GROUP_COLUMNS);
      filterByRequestBody.group_ids = groups
        .filter((group) => filterValues.includes(group.group_name))
        .map((group) => group.group_id);
    } else {
      setColumns(LABEL_COLUMNS);
      filterByRequestBody.type = labelType;
    }

    let requestBody = apiRequestBodyBuilder(state, filterByRequestBody);

    try {
      const response = await axios.post(url, requestBody);
      if (
        filterType == "labels" &&
        localStorage.getItem("platform") == "dummy_ads"
      ) {
        let newData = response.data.filter(
          (item) => filterValues.indexOf(item.label) != -1,
        );
        setData(newData);
      } else {
        setData(response.data);
      }
    } catch (err) {
      setData([]);
    } finally {
      setTotal({});
      setTotalShow(true);
    }
  };

  const fetchSegmentData = async (groupByValue) => {
    const filterType =
      appliedFilters?.length > 0 ? appliedFilters[0]?.type : "labels";
    const filterValues = appliedFilters.map((item) => item.value);

    let url =
      filterType === "labels"
        ? "https://api.confidanto.com/labels/segment-data"
        : "https://api.confidanto.com/labels-group/segment-data";

    let filterByRequestBody = {
      group_by: groupByValue,
      isGrouped: filterType !== "labels",
    };

    if (filterType === "labels") {
      filterByRequestBody.type = labelType;
      if (filterValues.length > 0) {
        filterByRequestBody.labels = filterValues;
      }
    } else if (filterType === "groups") {
      filterByRequestBody.group_ids = groups
        .filter((group) => filterValues.includes(group.group_name))
        .map((group) => group.group_id);
    }

    let requestBody = apiRequestBodyBuilder(state, filterByRequestBody);

    try {
      const response = await axios.post(url, requestBody);

      let updatedColumns =
        filterType === "labels" ? LABEL_SEGMENT_COLUMNS : GROUP_SEGMENT_COLUMNS;

      if (groupByValue !== "date") {
        updatedColumns = updatedColumns.map((item) => {
          if (item.key === "day_name") {
            return { ...item, visible: false };
          }
          return item;
        });
      }

      setColumns(updatedColumns);

      let formattedData = formatSegmentData(response.data, groupByValue);

      if (
        filterType === "labels" &&
        localStorage.getItem("platform") === "dummy_ads" &&
        filterValues.length > 0
      ) {
        formattedData = formattedData.filter(
          (item) => filterValues.indexOf(item.label) !== -1,
        );
      }

      setData(formattedData);
    } catch (err) {
      console.error("Fetch segment error:", err);
      setData([]);
    } finally {
      setTotal({});
      setTotalShow(true);
    }
  };

  const getCustomColumns = async () => {
    try {
      const requestBody = apiRequestBodyBuilder(null);

      const response = await axios.post(
        "https://api.confidanto.com/custom_columns/get-custom-columns",
        requestBody,
      );
      const temp = response.data.data.map((i) => ({
        id: i.id,
        title: i.custom_column,
        key: i.custom_column,
        visible: false,
        category: "Custom Columns",
        type: "number",
      }));
      setColumns((prevColumns) => {
        const prevColsFiltered = prevColumns.filter(
          (item) => item.category !== "Custom Columns",
        );
        const allColumns = [...prevColsFiltered, ...temp];
        const sorted = getPriorityColumnsFromStorage(allColumns);
        return sorted;
      });
    } catch (error) {
      setColumns((prevColumns) => {
        const sorted = getPriorityColumnsFromStorage(prevColumns);
        return sorted;
      });
      console.error("Error fetching custom columns:", error);
    }
  };

  const fetchCampaignNameData = async () => {
    try {
      let requestBody = apiRequestBodyBuilder(state, {
        name_contains: campaignNameInput,
      });

      const res = await axios.post(
        "https://api.confidanto.com/campaign-filter",
        requestBody,
      );
      setData(res.data);
      setColumns(CAMPAIGN_COLUMNS);
      setTotal({});
    } catch (error) {
      console.error("Error fetching campaign data by name:", error);
      setData([]);
    }
  };

  const changeOrderTypeCampaign = (type) => {
    const newOrderType =
      type === currentOrderVariable ? !currentOrderType : true;
    setCurrentOrderType(newOrderType);
    setCurrentOrderVariable(type);
    const sortedData = [...data].sort((a, b) => {
      const valA = getSortableValueGLOBAL(a, type);
      const valB = getSortableValueGLOBAL(b, type);

      if (valA == null) return 1;
      if (valB == null) return -1;
      const comparison =
        typeof valA === "number" && typeof valB === "number"
          ? valA - valB
          : String(valA).localeCompare(String(valB));
      return newOrderType ? comparison : -comparison;
    });
    setData(sortedData);
  };

  const toggleDropDownTabs = (type) => {
    setShowDatePicker(type === "Calendar" ? !showDatePicker : false);
    setShowSegmentDropdown(type === "Segment" ? !showSegmentDropdown : false);
    setShowFilterDropdown(type === "Filter" ? !showFilterDropdown : false);
    setShowColumnsMenu(type === "Columns" ? !showColumnsMenu : false);
    setShowDownloadMenu(type === "Download" ? !showDownloadMenu : false);
    setShowLabelTypeDropdown(
      type === "LabelType" ? !showLabelTypeDropdown : false,
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideMuiMenu = event.target.closest(
        ".groupwise-select-menu",
      );
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      )
        setShowDatePicker(false);
      if (segmentRef.current && !segmentRef.current.contains(event.target))
        setShowSegmentDropdown(false);
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target) &&
        !isClickInsideMuiMenu
      )
        setShowFilterDropdown(false);
      if (columnRef.current && !columnRef.current.contains(event.target))
        setShowColumnsMenu(false);
      if (downloadRef.current && !downloadRef.current.contains(event.target))
        setShowDownloadMenu(false);
      if (labelTypeRef.current && !labelTypeRef.current.contains(event.target))
        setShowLabelTypeDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    fetchLabels();
    setAppliedFilters((prev) => prev.filter((item) => item.type !== "labels"));
    setSelectedLabels([]);
  }, [labelType]);

  useEffect(() => {
    const loadAndProcessData = async () => {
      setLoading(true);
      try {
        if (segmentActive && selectedSegment) {
          await fetchSegmentData(selectedSegment);
        } else if (appliedFilters[0]?.type === "campaign") {
          await fetchCampaignNameData();
        } else {
          await fetchData(appliedFilters);
        }
        await getCustomColumns();
      } catch (error) {
        console.error("Error during data loading process:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    loadAndProcessData();
  }, [appliedFilters, random, segmentActive, selectedSegment, labelType]);

  const toggleFullScreen = () => setIsFullScreen(!isFullScreen);

  const applyFilter = (type) => {
    toggleDropDownTabs("Hide all");
    let newFilterArray = [];
    if (type === "campaign") {
      newFilterArray = [{ id: 0, type, value: campaignNameInput }];
    } else {
      const activeArray = type === "groups" ? selectedGroups : selectedLabels;
      newFilterArray = activeArray.map((item, index) => ({
        id: index,
        type,
        value: item,
      }));
    }
    setAppliedFilters(newFilterArray);
    setSelectedFilter(null);
    setSelectedGroups([]);
    setSelectedLabels([]);
  };

  const handleRemoveFilter = (filter) => {
    const newFilters = appliedFilters.filter(
      (item) => item.value !== filter.value,
    );
    setAppliedFilters(newFilters);
  };

  return (
    <div ref={exportRef}>
      <div
        className={`flex h-screen bg-white ${
          isFullScreen
            ? "fixed top-0 left-0 w-full h-full z-50 overflow-x-scroll"
            : ""
        }`}
      >
        <main className="flex flex-col flex-1 w-0 overflow-auto no-scrollbar p-6">
          <div
            className={`transition-opacity duration-300 ${loading ? "pointer-events-none opacity-50" : ""}`}
          >
            <DateRangeSelector
              state={state}
              setState={setState}
              setRandom={setRandom}
              showDatePicker={showDatePicker}
              toggleDropDownTabs={toggleDropDownTabs}
              datePickerRef={datePickerRef}
              Page="Promo Recap"
            />
          </div>
          <div className="flex justify-between items-center mb-4">
            <div
              className={`relative transition-opacity duration-300 ${loading ? "pointer-events-none opacity-50" : ""}`}
              ref={filterRef}
            >
              <div className="flex flex-row space-x-2">
                <button
                  disabled={loading}
                  className={`bg-transparent text-gray-600 px-4 py-2 rounded items-center flex transition-all ${
                    loading
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-slate-200"
                  }`}
                  onClick={() => toggleDropDownTabs("Filter")}
                >
                  <FaFilter className="mr-2" /> Add filter
                </button>
                <div className="flex items-center gap-2 flex-wrap">
                  {appliedFilters.map((filter, index) => (
                    <span
                      key={index}
                      className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-sm flex items-center"
                    >
                      {filter.value}
                      <button
                        disabled={loading}
                        className={`ml-2 text-blue-600 ${loading ? "cursor-not-allowed opacity-50" : "hover:text-blue-800"}`}
                        onClick={() => handleRemoveFilter(filter)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              {showFilterDropdown && (
                <div className="absolute left-0 mt-2 bg-white border p-3 shadow z-40 w-64">
                  {!selectedFilter ? (
                    <div>
                      <button
                        className="block w-full text-left px-2 py-1 hover:bg-gray-100"
                        onClick={() => setSelectedFilter("labels")}
                      >
                        Labels
                      </button>
                      <button
                        className="block w-full text-left px-2 py-1 hover:bg-gray-100"
                        onClick={() => setSelectedFilter("groups")}
                      >
                        Groups
                      </button>
                      <button
                        className="block w-full text-left px-2 py-1 hover:bg-gray-100"
                        onClick={() => setSelectedFilter("campaign")}
                      >
                        Campaign Name
                      </button>
                    </div>
                  ) : selectedFilter === "labels" ? (
                    <div className="mt-4">
                      <div>
                        {labels && labels.length > 0 ? (
                          <FormControl fullWidth>
                            <InputLabel>Labels</InputLabel>
                            <Select
                              multiple
                              value={selectedLabels}
                              onChange={(e) =>
                                setSelectedLabels(e.target.value)
                              }
                              input={<OutlinedInput label="Label Names" />}
                              renderValue={(selected) => selected.join(", ")}
                              MenuProps={{ className: "groupwise-select-menu" }}
                            >
                              {labels.map((label) => (
                                <MenuItem
                                  key={label.label_id}
                                  value={label.label_name}
                                >
                                  <Checkbox
                                    checked={selectedLabels.includes(
                                      label.label_name,
                                    )}
                                  />
                                  <ListItemText primary={label.label_name} />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <div className="flex flex-col items-center justify-center p-6 text-center">
                            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                              <FaLayerGroup className="text-gray-400 text-lg" />
                            </div>

                            <p className="text-sm font-medium text-gray-700">
                              No Labels available
                            </p>

                            <p className="mt-1 text-xs text-gray-500 leading-relaxed">
                              You don’t have any Labels created yet. Please
                              create a label to manage and apply group-based
                              ads.
                            </p>
                          </div>
                        )}

                        <div className="flex justify-end space-x-2 mt-2">
                          <Button onClick={() => setSelectedFilter(null)}>
                            Back
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            disabled={
                              !labels?.length || !selectedLabels?.length
                            }
                            onClick={() => applyFilter(selectedFilter)}
                          >
                            Apply
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : selectedFilter === "groups" ? (
                    <div className="mt-4">
                      {groups && groups.length > 0 ? (
                        <FormControl fullWidth>
                          <InputLabel>Groups</InputLabel>
                          <Select
                            multiple
                            value={selectedGroups}
                            onChange={(e) => setSelectedGroups(e.target.value)}
                            input={<OutlinedInput label="Group Names" />}
                            renderValue={(selected) => selected.join(", ")}
                            MenuProps={{ className: "groupwise-select-menu" }}
                          >
                            {groups.map((group) => (
                              <MenuItem
                                key={group.group_id}
                                value={group.group_name}
                              >
                                <Checkbox
                                  checked={selectedGroups.includes(
                                    group.group_name,
                                  )}
                                />
                                <ListItemText primary={group.group_name} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <div className="flex flex-col items-center justify-center p-6 text-center">
                          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                            <FaLayerGroup className="text-gray-400 text-lg" />
                          </div>

                          <p className="text-sm font-medium text-gray-700">
                            No groups available
                          </p>

                          <p className="mt-1 text-xs text-gray-500 leading-relaxed">
                            You don’t have any groups created yet. Please create
                            a group to manage and apply group-based ads.
                          </p>
                        </div>
                      )}
                      <div className="flex justify-end space-x-2 mt-2">
                        <Button onClick={() => setSelectedFilter(null)}>
                          Back
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={!groups?.length || !selectedGroups?.length}
                          onClick={() => applyFilter(selectedFilter)}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-600">
                        Campaign Name
                      </label>
                      <input
                        type="text"
                        value={campaignNameInput}
                        onChange={(e) => setCampaignNameInput(e.target.value)}
                        className="w-full border rounded px-2 py-1 text-sm"
                        placeholder="Enter campaign name"
                      />
                      <div className="flex justify-end space-x-2 mt-2">
                        <Button onClick={() => setSelectedFilter(null)}>
                          Back
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={!campaignNameInput?.trim()}
                          onClick={() => applyFilter(selectedFilter)}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex space-x-2">
              <div className="relative" ref={labelTypeRef}>
                <button
                  disabled={loading}
                  className={`px-4 py-2 rounded flex flex-col justify-center items-center transition-all ${
                    showLabelTypeDropdown
                      ? "bg-blue-100 text-blue-600"
                      : "bg-transparent text-gray-600"
                  } ${loading ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-100"}`}
                  onClick={() => toggleDropDownTabs("LabelType")}
                >
                  <MdOutlineLabel className="text-xl mb-1" />
                  <h2 className="whitespace-nowrap">
                    {labelType === "ads"
                      ? "Ads"
                      : labelType === "campaign"
                        ? "Campaigns"
                        : "Keywords"}
                  </h2>
                </button>
                {showLabelTypeDropdown && (
                  <div className="absolute right-0 z-40 w-44 bg-white shadow-lg rounded-lg mt-2 p-2 border">
                    {[
                      { value: "ads", label: "Ads Labels" },
                      { value: "campaign", label: "Campaigns Labels" },
                      { value: "keyword", label: "Keywords Labels" },
                    ].map((option) => (
                      <button
                        key={option.value}
                        className={`block w-full text-left px-4 py-2 rounded-sm ${
                          labelType === option.value
                            ? "bg-blue-100 text-blue-600"
                            : "hover:bg-gray-100"
                        }`}
                        onClick={() => {
                          setLabelType(option.value);
                          toggleDropDownTabs("Hide all");
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative" ref={segmentRef}>
                <button
                  disabled={loading}
                  className={`px-4 py-2 rounded flex flex-col justify-center items-center transition-all ${
                    segmentActive
                      ? "bg-blue-100 text-blue-600"
                      : "bg-transparent text-gray-600"
                  } ${loading ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-100"}`}
                  onClick={() => toggleDropDownTabs("Segment")}
                >
                  <MdOutlineSegment className="text-xl mb-1" />
                  <h2>Segment</h2>
                </button>
                {showSegmentDropdown && (
                  <div className="absolute right-0 z-40 w-56 bg-white shadow-lg rounded-lg mt-2 p-4 border">
                    <button
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100 rounded-sm"
                      onClick={() => {
                        setSelectedSegment(null);
                        setSegmentActive(false);
                        toggleDropDownTabs("Hide all");
                        setRandom(Math.random());
                      }}
                    >
                      Reset
                    </button>
                    {["date", "week", "month", "quarter", "year"].map(
                      (option) => (
                        <button
                          key={option}
                          className={`block w-full text-left px-4 py-2 rounded-sm ${
                            selectedSegment === option
                              ? "bg-blue-100 text-blue-600"
                              : "hover:bg-gray-100"
                          }`}
                          onClick={() => {
                            setSelectedSegment(option);
                            setSegmentActive(true);
                            toggleDropDownTabs("Hide all");
                          }}
                        >
                          {option.charAt(0).toUpperCase() + option.slice(1)}
                        </button>
                      ),
                    )}
                  </div>
                )}
              </div>

              <div
                className={`relative transition-opacity duration-300 ${loading ? "pointer-events-none opacity-50" : ""}`}
                ref={downloadRef}
              >
                <button
                  disabled={loading || isDownloading}
                  className={`px-4 py-2 rounded flex flex-col justify-center items-center transition-all ${
                    loading || isDownloading
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-slate-100"
                  } ${showDownloadMenu ? "bg-blue-100 text-blue-600" : "bg-transparent text-gray-600"}`}
                  onClick={() => toggleDropDownTabs("Download")}
                >
                  {isDownloading ? (
                    <svg
                      className="animate-spin h-5 w-5 mb-1"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <MdOutlineFileDownload className="text-xl mb-1" />
                  )}
                  <h2>{isDownloading ? "Downloading" : "Download"}</h2>
                </button>
                {showDownloadMenu && (
                  <div className="absolute right-0 z-40 w-36 bg-white shadow-lg rounded-lg mt-2 p-2 border">
                    <div
                      className="p-2 hover:bg-gray-100 cursor-pointer rounded flex items-center justify-between text-gray-700"
                      onClick={() => handleDownload("pdf")}
                    >
                      .PDF
                    </div>
                    <div
                      className="p-2 hover:bg-gray-100 cursor-pointer rounded flex items-center justify-between text-gray-700"
                      onClick={() => handleDownload("csv")}
                    >
                      .CSV
                    </div>
                  </div>
                )}
              </div>

              <button
                disabled={loading}
                className={`px-4 py-2 rounded flex flex-col justify-center items-center transition-all ${
                  isFullScreen
                    ? "bg-blue-100 text-blue-600"
                    : "bg-transparent text-gray-600"
                } ${loading ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-100"}`}
                onClick={toggleFullScreen}
              >
                {isFullScreen ? (
                  <FaCompress className="mb-1" />
                ) : (
                  <FaExpand className="mb-1" />
                )}
                <h2>{isFullScreen ? "Collapse" : "Expand"}</h2>
              </button>
              <div className="relative" ref={columnRef}>
                <button
                  disabled={loading}
                  className={`bg-transparent text-gray-600 px-4 py-2 rounded items-center flex flex-col transition-all ${
                    loading
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-slate-200"
                  }`}
                  onClick={() => toggleDropDownTabs("Columns")}
                >
                  <FaColumns className="mb-1" />
                  <span>Columns</span>
                </button>
                {showColumnsMenu && (
                  <ModifyColumns
                    columns={columns}
                    setColumns={setColumns}
                    setTableVisible={setTableVisible}
                    setShowColumnsMenu={setShowColumnsMenu}
                    setTotal={setTotal}
                    setTotalShow={setTotalShow}
                  />
                )}
              </div>
            </div>
          </div>
          {tableVisible && (
            <div className="overflow-x-auto w-full">
              {/* Table & Skeleton Loading */}
              {loading ? (
                <TableSkeleton />
              ) : data.length > 0 ? (
                <RenderTable
                  columns={columns}
                  data={data}
                  variant="performance"
                  enableExpandCollapse={state.length > 1}
                />
              ) : (
                <div className="flex justify-center items-center h-60 mt-8">
                  <h2>No Data Found</h2>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default PromoRecap;
